'use client';

import React, { FC, Fragment, PropsWithChildren, useState } from 'react';

import SearchQuery from '@/src/components/SearchQuery';
import { IStoreDepartment, IStoresIndustryGroup } from '@/src/types/stores';
import { useLocale, useTranslations } from 'next-intl';
import useNavigation from '@/src/lib/hooks/useNavigation';
import { useSearchParams } from 'next/navigation';
import { cn } from '@/src/lib/utils/command';
import { CirclePercent, Ellipsis, ListRestart, ScaleIcon } from 'lucide-react';
import PillIcon from '@/src/components/icons/Pill';
import BeautyAndCareIcon from '@/src/components/icons/BeautyAndCareIcon';
import MakeupIcon from '@/src/components/icons/MakupIcon';
import PerfumeIcon from '@/src/components/icons/PerfumeIcon';
import GiftIcon from '@/src/components/icons/GiftIcon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/src/components/ui/select';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from '@/src/components/ui/drawer';
import { useRouter, usePathname } from '@/src/i18n/navigation';
import { CategoryFilter } from './CategoryFilter';
import { Button } from '@/src/components/ui/button';
import Image from 'next/image';

type Props = {
  industries: IStoreDepartment[];
};

type TFilterButton = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  isActive?: boolean;
};

enum filterNumberIcons {
  PIll = 155,
  BeautyAndCare = 143,
  Makeup = 149,
  PerfumeIcon = 164,
  Gift = 174,
}

const FilterIcon: { [key: number]: { icon: any } } = {
  155: { icon: <PillIcon /> },
  143: { icon: <BeautyAndCareIcon /> },
  149: { icon: <MakeupIcon /> },
  164: { icon: <PerfumeIcon /> },
  174: { icon: <GiftIcon /> },
};

// button filter
const FilterButton: FC<PropsWithChildren<TFilterButton>> = ({
  children,
  isActive = false,
  ...props
}) => {
  return (
    <Button {...props} size={'sm'} variant={isActive ? 'default' : 'secondary'}>
      {children}
    </Button>
  );
};

// Dropdown Filter

const findIndustryById = (data: IStoresIndustryGroup[], id: number) =>
  data.flatMap((group) => group.industries).find((industry) => industry.id === id) || null;

const StoresFilter: FC<Props> = ({ industries }) => {
  const { createQueryString, removeQueryString } = useNavigation();
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations();
  const params = useSearchParams();
  const hasOffers = params.get('hasOffers');
  const orderType = params.get('orderType');

  const handleHasOffers = () => {
    if (hasOffers === 'true') {
      removeQueryString('hasOffers');
      return;
    }
    createQueryString({ hasOffers: 'true' });
  };

  const handleGiftClick = () => {
    if (orderType === '3') {
      removeQueryString('orderType');
      return;
    }
    createQueryString({ orderType: '3' });
  };

  const handeRestetFilter = () => {
    router.replace(pathname);
  };

  return (
    <div>
      {/* start search input  */}
      <div className="my-4">
        <SearchQuery queryName="q" placeholder={t('dialog.SearchForStores')} />
      </div>

      <div className="flex max-w-full items-center justify-between">
        {/* filter by industry  */}
        <div className="styled-scroll grid auto-cols-max grid-flow-col gap-2 overflow-x-auto">
          <CategoryFilter fetchedCategories={industries} />

          <FilterButton onClick={handleHasOffers} isActive={hasOffers === 'true'}>
            <CirclePercent className="me-1 size-4 text-rose-500" />
            {t('stores.offers')}
          </FilterButton>

          <FilterButton onClick={handleGiftClick} isActive={orderType === '3'}>
            <Image src={'/giftIcon.svg'} alt="gift" width={24} height={24} />
            {t('stores.Gift')}
          </FilterButton>
        </div>

        {/* rest filter  */}
        <div>
          <button onClick={() => handeRestetFilter()}>
            <ListRestart />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoresFilter;
